/**
 * Add Expense to Project Modal
 * Quick expense entry modal with domain-specific category shortcuts
 * Task 33: Project Management - Add Expense to Project shortcut
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePaymentAccounts } from '@/hooks/usePaymentAccounts';
import { useTransaction } from '@/hooks/useTransaction';
import { CompanyType } from '@/types/company';
import type { IndustryProject } from '@/types/construction';
import { cn } from '@/lib/utils';

// Category presets by domain
const CATEGORY_PRESETS: Record<CompanyType, Array<{ id: number; name: string; icon: string }>> = {
  [CompanyType.CONSTRUCTION]: [
    { id: 1, name: 'Material', icon: '🧱' },
    { id: 2, name: 'Mão de Obra', icon: '👷' },
    { id: 3, name: 'Equipamento', icon: '🚧' },
    { id: 4, name: 'Transporte', icon: '🚛' },
  ],
  [CompanyType.AGENCIES]: [
    { id: 5, name: 'Design', icon: '🎨' },
    { id: 6, name: 'Desenvolvimento', icon: '💻' },
    { id: 7, name: 'Marketing', icon: '📱' },
    { id: 8, name: 'Mídia', icon: '📺' },
  ],
  [CompanyType.ARCHITECTS_ENGINEERS]: [
    { id: 9, name: 'Projeto', icon: '📐' },
    { id: 10, name: 'Consultoria', icon: '🏛️' },
    { id: 11, name: 'Vistorias', icon: '🔍' },
    { id: 12, name: 'Software', icon: '💻' },
  ],
  [CompanyType.CONSULTING_B2B]: [
    { id: 13, name: 'Consultoria', icon: '💼' },
    { id: 14, name: 'Treinamento', icon: '🎓' },
    { id: 15, name: 'Software', icon: '💻' },
    { id: 16, name: 'Viagens', icon: '✈️' },
  ],
  [CompanyType.IT_COMPANIES]: [
    { id: 17, name: 'Desenvolvimento', icon: '💻' },
    { id: 18, name: 'Infraestrutura', icon: '🖥️' },
    { id: 19, name: 'Licenças', icon: '📜' },
    { id: 20, name: 'Cloud', icon: '☁️' },
  ],
  [CompanyType.PHOTOGRAPHERS_VIDEOGRAPHERS]: [
    { id: 21, name: 'Equipamento', icon: '📷' },
    { id: 22, name: 'Edição', icon: '🎬' },
    { id: 23, name: 'Locação', icon: '🏢' },
    { id: 24, name: 'Transporte', icon: '🚗' },
  ],
  [CompanyType.EVENT_PRODUCERS]: [
    { id: 25, name: 'Locação', icon: '🏢' },
    { id: 26, name: 'Fornecedores', icon: '🍽️' },
    { id: 27, name: 'Decoração', icon: '🎊' },
    { id: 28, name: 'Equipe', icon: '👥' },
  ],
  // Default fallback for other types
  [CompanyType.HEALTH_CLINICS]: [],
  [CompanyType.LAW_FIRMS]: [],
  [CompanyType.EDUCATION_PROFESSIONALS]: [],
  [CompanyType.PILATES_YOGA_STUDIOS]: [],
  [CompanyType.VETERINARY_CLINICS]: [],
  [CompanyType.BEAUTY_CLINICS]: [],
  [CompanyType.AUTO_REPAIR_SHOPS]: [],
  [CompanyType.BEAUTY_SALONS]: [],
};

const expenseSchema = z.object({
  description: z.string().min(1, 'Descrição é obrigatória'),
  amount: z.number().positive('Valor deve ser maior que zero'),
  due_date: z.date(),
  category_id: z.number().optional(),
  payment_account_id: z.number().optional(),
  notes: z.string().optional(),
});

type ExpenseFormData = z.infer<typeof expenseSchema>;

interface AddExpenseToProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: IndustryProject;
  domain: CompanyType;
}

export function AddExpenseToProjectModal({
  open,
  onOpenChange,
  project,
  domain,
}: AddExpenseToProjectModalProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>();
  const { paymentAccounts } = usePaymentAccounts();
  const { create: createTransaction } = useTransaction();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ExpenseFormData>({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      due_date: new Date(),
      amount: 0,
    },
  });

  const categoryPresets = CATEGORY_PRESETS[domain] || [];
  const dueDate = watch('due_date');

  const handleCategoryClick = (categoryId: number) => {
    setSelectedCategoryId(categoryId);
    setValue('category_id', categoryId);
  };

  const onSubmit = async (data: ExpenseFormData) => {
    try {
      await createTransaction({
        type: 'expense',
        description: data.description,
        amount: data.amount,
        due_date: format(data.due_date, 'yyyy-MM-dd'),
        category_id: data.category_id,
        payment_account_id: data.payment_account_id,
        notes: data.notes,
        // @ts-ignore - industry_project_id will be added after migration
        industry_project_id: project.id,
        status: 'pending',
      });

      reset();
      setSelectedCategoryId(undefined);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating expense:', error);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedCategoryId(undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle>Adicionar Despesa ao Projeto</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {project.name}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full"
              onClick={handleClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Quick Category Buttons */}
          {categoryPresets.length > 0 && (
            <div className="space-y-2">
              <Label>Categoria Rápida</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {categoryPresets.map((category) => (
                  <Button
                    key={category.id}
                    type="button"
                    variant={selectedCategoryId === category.id ? 'default' : 'outline'}
                    className="h-auto py-3 flex flex-col items-center gap-2"
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    <span className="text-2xl">{category.icon}</span>
                    <span className="text-xs">{category.name}</span>
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              Descrição <span className="text-destructive">*</span>
            </Label>
            <Input
              id="description"
              {...register('description')}
              placeholder="Ex: Compra de materiais de construção"
              className={cn(errors.description && 'border-destructive')}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">
                Valor <span className="text-destructive">*</span>
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                {...register('amount', { valueAsNumber: true })}
                placeholder="0,00"
                className={cn(errors.amount && 'border-destructive')}
              />
              {errors.amount && (
                <p className="text-sm text-destructive">{errors.amount.message}</p>
              )}
            </div>

            {/* Due Date */}
            <div className="space-y-2">
              <Label>
                Data de Vencimento <span className="text-destructive">*</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !dueDate && 'text-muted-foreground'
                    )}
                  >
                    {dueDate ? format(dueDate, 'dd/MM/yyyy') : 'Selecione a data'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={(date) => date && setValue('due_date', date)}
                    locale={require('date-fns/locale/pt-BR').ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Payment Account */}
          <div className="space-y-2">
            <Label htmlFor="payment_account_id">Conta de Pagamento</Label>
            <Select
              value={watch('payment_account_id')?.toString()}
              onValueChange={(value) => setValue('payment_account_id', parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma conta (opcional)" />
              </SelectTrigger>
              <SelectContent>
                {paymentAccounts?.map((account) => (
                  <SelectItem key={account.id} value={account.id.toString()}>
                    {account.account_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Observações</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Informações adicionais (opcional)"
              rows={3}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit">Adicionar Despesa</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
